import { isFeatureAllowedForICPAccount } from 'modules/accounts/utils/isFeatureAllowedForICPAccount';
import { urlValidations } from 'modules/campaigns/config';
import { setSelectedRows } from 'modules/campaigns/modules/campaigns-reports/actions';
import {
    CheckboxCellRenderer,
    CheckboxHeaderRenderer,
    ContentCellRenderer,
    CreativeIdCellRenderer,
    CreativeLibraryActionsCellRenderer,
    CreativeLibraryStateCellRenderer,
    CreativeStatusRenderer,
    EllipsisCellRenderer,
    FormattedMessageHeaderRenderer,
    IMAGE_CELL_RENDERER_MODES,
    ImageCellRenderer,
    LinkCellRenderer,
    withCreativeEditIndication,
} from 'modules/campaigns/modules/campaigns-reports/components';
import {
    actionsColumnType,
    bypassMetadataFilterType,
    checkBoxSelectionColumn,
    editableColumn,
    leftPinnedColumn,
    lockedColumn,
    numericColumn,
    rightPinnedColumn,
    staticColumn,
    toggleColumn,
} from 'modules/campaigns/modules/campaigns-reports/config/columns';
import * as FIELDS from 'modules/campaigns/modules/campaigns-reports/config/fields';
import { nonDefaultHeaderComponentParams } from 'modules/campaigns/modules/campaigns-reports/config/fields';
import { isCreativeRejected, isHTMLCardCreative } from 'modules/campaigns/modules/campaigns-reports/config/predicates';
import { updateLibraryCreative } from 'modules/campaigns/modules/campaigns-reports/flows';
import { updateLibraryCreativeUrlCellEditor } from 'modules/campaigns/modules/campaigns-reports/flows/updateLibraryCreativeUrlCellEditor';
import { normalizeColumnDefs } from 'modules/campaigns/modules/campaigns-reports/utils';
import { BYPASS_URL_RESPONSE_VALIDATION_FIELD } from 'modules/campaigns/modules/creative-creator/components/UrlList/UrlValidatedInput';
import { deleteLibraryCreative } from 'modules/creative-library/flows';
import { duplicateLibraryCreative } from 'modules/creative-library/flows/duplicateLibraryCreative';
import { editLibraryCreative } from 'modules/creative-library/flows/editLibraryCreative';
import { FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import { CREATIVE_LIBRARY_REPORT_ID } from './creativeLibraryReportId';

const creativeLibraryReportColumnDefinition = [
    {
        headerComponent: CheckboxHeaderRenderer,
        headerName: FIELDS.IS_SELECTED.label,
        field: FIELDS.IS_SELECTED.field,
        type: [leftPinnedColumn, checkBoxSelectionColumn, staticColumn],
        cellRendererParams: {
            onClick: setSelectedRows,
        },
        headerComponentParams: {
            onClick: setSelectedRows,
        },
        valueGetter: FIELDS.IS_SELECTED.valueGetter,
        cellRenderer: CheckboxCellRenderer,
        csv: false,
        permissions: {
            permissions: ['CAMPAIGNS_ITEMS_BULK_UPDATE'],
        },
        accountConfigurations: {
            [FEATURE_FLAGS.CREATIVES_BULK_ACTIONS_ENABLED]: 'true',
        },
    },
    {
        headerName: FIELDS.IS_ACTIVE.label,
        field: FIELDS.IS_ACTIVE.field,
        type: [lockedColumn, toggleColumn, leftPinnedColumn, staticColumn],
        cellRendererParams: {
            ...FIELDS.IS_ACTIVE.renderParams,
            isDisabled: data => isCreativeRejected(data),
            reportId: CREATIVE_LIBRARY_REPORT_ID,
        },
        cellRenderer: CreativeLibraryStateCellRenderer,
        valueChangeHandler: updateLibraryCreative,
        csv: false,
    },
    {
        headerName: FIELDS.THUMBNAIL_URL.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.THUMBNAIL_URL.headerComponentParams,
        field: FIELDS.THUMBNAIL_URL.field,
        type: [leftPinnedColumn, bypassMetadataFilterType],
        valueGetter: ({ data }) => {
            const { thumbnailUrl } = data;
            return thumbnailUrl;
        },
        cellRenderer: ImageCellRenderer,
        cellRendererParams: {
            mode: IMAGE_CELL_RENDERER_MODES.COMFORTABLE,
        },
        width: 186,
        csv: FIELDS.THUMBNAIL_URL.csv,
    },
    {
        headerName: FIELDS.CUSTOM_CREATIVE_NAME.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.CUSTOM_CREATIVE_NAME.headerComponentParams,
        field: FIELDS.CUSTOM_CREATIVE_NAME.field,
        type: [leftPinnedColumn, editableColumn],
        maxWidth: 260,
        cellRenderer: EllipsisCellRenderer,
        cellRendererParams: { lines: 2 },
        cellEditor: FIELDS.CUSTOM_CREATIVE_NAME.cellEditor,
        valueChangeHandler: updateLibraryCreative,
        valueFormatter: FIELDS.CUSTOM_CREATIVE_NAME.valueFormatter,
        accountConfigurations: { [FEATURE_FLAGS.SHOW_CUSTOM_DATA_IN_CREATIVE_FORM]: 'true' },
    },
    {
        headerName: FIELDS.AD_GROUPS_ASSIGNMENT.label,
        field: FIELDS.AD_GROUPS_ASSIGNMENT.field,
        cellRenderer: EllipsisCellRenderer,
        headerValueGetter: FormattedMessageHeaderRenderer,
        cellRendererParams: { isSearchable: true },
        headerComponentParams: FIELDS.AD_GROUPS_ASSIGNMENT.headerComponentParams,
        cellEditor: FIELDS.AD_GROUPS_ASSIGNMENT.cellEditor,
        valueFormatter: FIELDS.AD_GROUPS_ASSIGNMENT.valueFormatter,
    },
    {
        headerName: FIELDS.ID.label,
        field: FIELDS.ID.field,
        type: [numericColumn],
        cellRenderer: CreativeIdCellRenderer,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: nonDefaultHeaderComponentParams(CREATIVE_LIBRARY_REPORT_ID),
        cellRendererParams: { isSearchable: true },
    },
    {
        headerName: FIELDS.STATUS.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.STATUS.headerComponentParams,
        field: FIELDS.STATUS.field,
        cellRenderer: CreativeStatusRenderer,
        cellRendererParams: {
            ...FIELDS.STATUS.renderParams,
            isInCreativeLibrary: true,
        },
        csvValueGetter: FIELDS.STATUS.csvValueGetter,
    },
    {
        headerName: FIELDS.CONTENT.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.CONTENT.headerComponentParams,
        type: [bypassMetadataFilterType],
        valueGetter: ({ data }) => {
            const { title, description, cta } = data;
            return {
                title,
                description,
                cta,
            };
        },
        field: FIELDS.CONTENT.field,
        cellRenderer: ContentCellRenderer,
        cellRendererParams: {
            lines: 2,
            ctaMsgIdPrefix: 'creative.creator.content.cta.option',
            isSearchable: true,
        },
        width: 328,
        csv: [
            {
                headerName: 'Title',
                field: 'title',
            },
            {
                headerName: 'Description',
                field: 'description',
            },
            ...FIELDS.CTA.csv,
        ],
    },
    {
        headerName: FIELDS.URL.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.URL.headerComponentParams,
        field: FIELDS.URL.field,
        type: [editableColumn],
        cellRenderer: LinkCellRenderer,
        width: 270,
        cellEditor: FIELDS.URL.cellEditor,
        cellEditorParams: {
            validations: urlValidations,
        },
        valueChangeHandler: updateLibraryCreativeUrlCellEditor,
        cellRendererParams: { isSearchable: true },
        valueSetter: params => {
            params.data[FIELDS.URL.field] = params.newValue.url;
            params.data[BYPASS_URL_RESPONSE_VALIDATION_FIELD] = params.newValue[BYPASS_URL_RESPONSE_VALIDATION_FIELD];
            return true;
        },
    },
    {
        headerName: FIELDS.CHANNEL_TYPE.label,
        field: FIELDS.CHANNEL_TYPE.field,
        cellRendererParams: FIELDS.CHANNEL_TYPE.renderParams,
        cellRenderer: FIELDS.CHANNEL_TYPE.cellRenderer,
        csvValueGetter: FIELDS.CHANNEL_TYPE.csvValueGetter,
        visibilityCondition: ({ selectedAccount, selectedAccountConfig }) =>
            isFeatureAllowedForICPAccount(
                selectedAccount,
                selectedAccountConfig,
                FEATURE_FLAGS.SHOULD_SHOW_DISPLAY_DATA
            ),
    },
    {
        headerName: FIELDS.FORMAT.label,
        field: FIELDS.FORMAT.field,
        cellRendererParams: FIELDS.FORMAT.renderParams,
        cellRenderer: FIELDS.FORMAT.cellRenderer,
        csvValueGetter: FIELDS.FORMAT.csvValueGetter,
        valueGetter: ({ data }) => {
            const { format } = data;
            if (isHTMLCardCreative(data)) {
                return data.displayData.displayAdType;
            }
            return format;
        },
        visibilityCondition: ({ selectedAccount, selectedAccountConfig }) =>
            isFeatureAllowedForICPAccount(
                selectedAccount,
                selectedAccountConfig,
                FEATURE_FLAGS.SHOULD_SHOW_DISPLAY_DATA
            ),
    },
    {
        headerName: FIELDS.SIZE.label,
        field: FIELDS.SIZE.field,
        cellRenderer: FIELDS.SIZE.cellRenderer,
        valueFormatter: FIELDS.SIZE.valueFormatter,
        valueGetter: FIELDS.SIZE.valueGetter,
        csv: FIELDS.SIZE.csv,
        visibilityCondition: ({ selectedAccount, selectedAccountConfig }) =>
            isFeatureAllowedForICPAccount(
                selectedAccount,
                selectedAccountConfig,
                FEATURE_FLAGS.SHOULD_SHOW_DISPLAY_DATA
            ),
    },
    {
        headerName: FIELDS.CREATIVE_ORIENTATION.label,
        field: FIELDS.CREATIVE_ORIENTATION.field,
        cellRendererParams: FIELDS.CREATIVE_ORIENTATION.renderParams,
        cellRenderer: FIELDS.CREATIVE_ORIENTATION.cellRenderer,
        csvValueGetter: FIELDS.CREATIVE_ORIENTATION.csvValueGetter,
    },
    {
        headerName: FIELDS.CREATE_TIME.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.CREATE_TIME.headerComponentParams,
        field: FIELDS.CREATE_TIME.field,
        valueFormatter: FIELDS.CREATE_TIME.valueFormatter,
        csvValueGetter: FIELDS.CREATE_TIME.csvValueGetter,
    },
    {
        headerName: FIELDS.CUSTOM_ID.label,
        field: FIELDS.CUSTOM_ID.field,
        cellRenderer: EllipsisCellRenderer,
        cellRendererParams: { lines: 2 },
        cellEditor: FIELDS.CUSTOM_ID.cellEditor,
        maxWidth: 260,
        valueFormatter: FIELDS.CUSTOM_ID.valueFormatter,
        accountConfigurations: { [FEATURE_FLAGS.SHOW_CUSTOM_DATA_IN_CREATIVE_FORM]: 'true' },
    },
    {
        headerName: FIELDS.ACTIONS_COLUMN.label,
        field: FIELDS.ACTIONS_COLUMN.field,
        type: [rightPinnedColumn, actionsColumnType, staticColumn],
        minWidth: 120,
        maxWidth: 120,
        width: 120,
        cellRendererParams: {
            onDelete: deleteLibraryCreative,
            onPreview: () => {
                return { type: 'NO_OP_PREVIEW' };
            },
            onEdit: editLibraryCreative,
            onDuplicate: duplicateLibraryCreative,
        },
        cellRenderer: CreativeLibraryActionsCellRenderer,
        csv: false,
    },
];

export const availableColumnDefinitions = normalizeColumnDefs(
    creativeLibraryReportColumnDefinition,
    CREATIVE_LIBRARY_REPORT_ID,
    withCreativeEditIndication
);
